import { z } from 'zod'
import { DossError } from '../utils/doss-error.js'
import { identitySystemEvents } from './identity.js'
import { integrationSystemEvents } from './integrations.js'
import { interactionSystemEvents } from './interactions.js'
import { normalizeLegacySystemEventFailureData } from './legacy-failure.js'
import type { SystemEventStatus } from './primitives.js'
import { tableSystemEvents } from './tables.js'
import { workflowSystemEvents } from './workflows.js'

export type { SystemEventMembershipChangeVia } from './identity.js'

/** Add definitions in the domain modules; all public types and validators derive from this catalog. */
export const systemEvents = {
  ...tableSystemEvents,
  ...interactionSystemEvents,
  ...integrationSystemEvents,
  ...identitySystemEvents,
  ...workflowSystemEvents,
}

const definitions = Object.values(systemEvents)
const successDefinitions = definitions.filter((definition) => definition.status === 'SUCCESS')
const failureDefinitions = definitions.filter((definition) => definition.status === 'FAIL')

const unionByEventType = <TSchema extends z.ZodObject>(
  schemas: TSchema[],
): z.ZodDiscriminatedUnion<[TSchema, ...TSchema[]], 'eventType'> => {
  // Zod's discriminated union requires a statically non-empty tuple of schemas.
  const [firstSchema, ...remainingSchemas] = schemas
  if (!firstSchema) {
    throw new DossError('Each event status must have at least one definition', [])
  }
  return z.discriminatedUnion('eventType', [firstSchema, ...remainingSchemas])
}

export const systemEventTypeSchema = z.enum([
  ...new Set(definitions.map((definition) => definition.type)),
])
export type SystemEventType = z.infer<typeof systemEventTypeSchema>

// Both fields select the schema: one event type can have different success/failure payloads and targets.
export const systemEventDescriptionSchema = z.discriminatedUnion('eventStatus', [
  unionByEventType(successDefinitions.map((definition) => definition.descriptionSchema)),
  unionByEventType(failureDefinitions.map((definition) => definition.descriptionSchema)),
])
export type SystemEventDescription = z.infer<typeof systemEventDescriptionSchema>

/**
 * Completed application event, corresponding to PR #10769's ClickHouse columns in camelCase.
 * data remains a typed JSON object here; storage encoding and data_json derivation belong to the writer.
 */
export const systemEventSchema = z.discriminatedUnion('eventStatus', [
  unionByEventType(successDefinitions.map((definition) => definition.eventSchema)),
  unionByEventType(failureDefinitions.map((definition) => definition.eventSchema)),
])
export type SystemEvent = z.infer<typeof systemEventSchema>

/** Ingress accepts pre-migration batches; producer descriptions remain strict and canonical. */
export const systemEventIngressSchema = z.preprocess((value) => {
  if (
    value === null ||
    typeof value !== 'object' ||
    !('eventStatus' in value) ||
    value.eventStatus !== 'FAIL'
  ) {
    return value
  }
  if (
    !('data' in value) ||
    value.data === null ||
    typeof value.data !== 'object' ||
    Array.isArray(value.data)
  ) {
    return value
  }
  return {
    ...value,
    data: normalizeLegacySystemEventFailureData(value.data as Record<string, unknown>),
  }
}, systemEventSchema)

export type SystemEventOf<
  TType extends SystemEventType,
  TStatus extends SystemEventStatus = SystemEventStatus,
> = Extract<SystemEvent, { eventType: TType; eventStatus: TStatus }>
export type SystemEventDescriptionOf<
  TType extends SystemEventType,
  TStatus extends SystemEventStatus = SystemEventStatus,
> = Extract<SystemEventDescription, { eventType: TType; eventStatus: TStatus }>
export type SystemEventData<
  TType extends SystemEventType,
  TStatus extends SystemEventStatus = SystemEventStatus,
> = SystemEventOf<TType, TStatus>['data']
